import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_BLOB_URL_BYTES = 50 * 1024 * 1024;

type UseObjectUrlResult = {
  url: string | null;
  error: Error | null;
};

export const useObjectUrl = (
  blob?: Blob,
  maxBlobUrlBytes: number = MAX_BLOB_URL_BYTES,
): UseObjectUrlResult => {
  const [state, setState] = useState<UseObjectUrlResult>({
    url: null,
    error: null,
  });

  const urlRef = useRef<string | null>(null);

  const revokeCurrentUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return revokeCurrentUrl;
  }, [revokeCurrentUrl]);

  useEffect(() => {
    if (!blob) {
      revokeCurrentUrl();
      setState({ url: null, error: null });
      return;
    }

    if (blob.size > maxBlobUrlBytes) {
      revokeCurrentUrl();
      setState({
        url: null,
        error: new Error('Preview generation failed'),
      });
      return;
    }

    revokeCurrentUrl();
    const newUrl = URL.createObjectURL(blob);
    urlRef.current = newUrl;
    setState({ url: newUrl, error: null });

    return () => {
      URL.revokeObjectURL(newUrl);
      urlRef.current = null;
    };
  }, [blob, maxBlobUrlBytes, revokeCurrentUrl]);

  return state;
};
